import { useDebugStore, type DebugLogLevel } from "@/stores/useDebugStore";
import { useAuthStore } from "@/stores/useAuthStore";
import { can, viewTier } from "@/lib/tier";

/**
 * Debug mode is on AND the signed-in tier may see on-chain detail. A Simple
 * session never collects, so toggling the flag cannot surface it later.
 */
export function debugCollectionAllowed(): boolean {
  if (!useDebugStore.getState().enabled) return false;
  const s = useAuthStore.getState().session;
  return can(viewTier(s?.role, s?.tier), "onchainDetail");
}

/**
 * Log a debug entry when debug mode is enabled.
 * Also mirrors to the browser console with a [DEBUG] prefix for DevTools visibility.
 *
 * Safe to call anywhere — silently no-ops when debug mode is off.
 */
export function debugLog(
  level: DebugLogLevel,
  category: string,
  message: string,
  meta?: Record<string, unknown>,
): void {
  if (!debugCollectionAllowed()) return;
  const { addLog } = useDebugStore.getState();

  try {
    addLog({ level, category, message, meta });
  } catch {
    // Debug failures must never break the app
  }

  const tag = `[DEBUG][${category}]`;
  switch (level) {
    case "error":
      console.error(tag, message, meta ?? "");
      break;
    case "warn":
      console.warn(tag, message, meta ?? "");
      break;
    default:
      console.log(tag, message, meta ?? "");
  }
}
